import React, { useState, useEffect } from 'react';
import { Drawer, Button, Typography, Spin, Alert, Badge, Tooltip } from 'antd';
import { BugOutlined, ReloadOutlined, ClearOutlined } from '@ant-design/icons';
import TaskList from './TaskList';
import { fetchUserTasks, fetchUserTaskCounts } from './services/taskService';

const { Title } = Typography;

const TaskDrawer = ({ visible, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [taskCounts, setTaskCounts] = useState({});
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Fetch tasks based on selected status
  const loadTasks = async (status = selectedStatus) => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      if (status === 'all') {
        // Fetch all tasks regardless of status
        const allStatuses = ['active', 'waiting', 'completed', 'failed'];
        const allResponses = await Promise.all(
          allStatuses.map(s => fetchUserTasks(s))
        );
        
        // Combine all tasks
        const allTasks = [];
        allResponses.forEach(res => {
          if (res.success && res.jobs) {
            allTasks.push(...res.jobs);
          }
        });
        
        // Sort by timestamp (newest first)
        allTasks.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        
        setTasks(allTasks);
        setLastRefresh(new Date());
      } else {
        response = await fetchUserTasks(status);
        if (response.success) {
          // Extract tasks from the jobs array in the response
          setTasks(response.jobs || []);
          setLastRefresh(new Date());
        } else {
          setError(response.message || 'Failed to fetch tasks');
        }
      }
    } catch (err) {
      console.error('Error loading tasks:', err);
      setError(err.message || 'Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Fetch task counts for all statuses
  const loadTaskCounts = async () => {
    try {
      const response = await fetchUserTaskCounts();
      if (response.success) {
        // Check if counts are in response.data or directly in response
        setTaskCounts(response.data || response.counts || {});
      }
    } catch (err) {
      console.error('Error loading task counts:', err);
    }
  };

  // Load initial data when drawer opens
  useEffect(() => {
    if (visible) {
      loadTasks();
      loadTaskCounts();
    }
  }, [visible, selectedStatus]);

  // Handle status tab change
  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    loadTasks(status);
  };

  // Handle manual refresh
  const handleRefresh = () => {
    loadTasks();
    loadTaskCounts();
  };

  // Clear completed tasks (placeholder for future implementation)
  const handleClearCompleted = () => {
    // TODO: Implement clear completed tasks
    console.log('Clear completed tasks');
  };

  const statusTabs = [
    { key: 'all', label: 'All', count: (taskCounts.active || 0) + (taskCounts.waiting || 0) + (taskCounts.completed || 0) + (taskCounts.failed || 0) },
    { key: 'active', label: 'Active', count: taskCounts.active || 0 },
    { key: 'waiting', label: 'Waiting', count: taskCounts.waiting || 0 },
    { key: 'completed', label: 'Completed', count: taskCounts.completed || 0 },
    { key: 'failed', label: 'Failed', count: taskCounts.failed || 0 }
  ];

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <BugOutlined style={{ color: '#1890ff' }} />
          <span>Task Monitor</span>
          {lastRefresh && (
            <Tooltip title={`Last refreshed: ${lastRefresh.toLocaleTimeString()}`}>
              <Badge 
                status="success" 
                text="Live"
                style={{ marginLeft: 8, fontSize: 12 }}
              />
            </Tooltip>
          )}
        </div>
      }
      open={visible}
      onClose={onClose}
      width={400}
      placement="right"
      extra={
        <div style={{ display: 'flex', gap: 8 }}>
          <Tooltip title="Refresh tasks">
            <Button 
              icon={<ReloadOutlined />} 
              size="small" 
              onClick={handleRefresh}
              loading={loading}
            />
          </Tooltip>
          <Tooltip title="Clear completed">
            <Button 
              icon={<ClearOutlined />} 
              size="small" 
              onClick={handleClearCompleted}
              disabled={!taskCounts.completed}
            />
          </Tooltip>
        </div>
      }
      styles={{
        body: { padding: 0 },
        header: { borderBottom: '1px solid #f0f0f0' }
      }}
    >
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Status Tabs */}
        <div style={{ 
          padding: '16px 16px 0', 
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: '#fafafa'
        }}>
          <div style={{ display: 'flex', gap: 4, width: '100%' }}>
            {statusTabs.map(tab => (
              <Button
                key={tab.key}
                type={selectedStatus === tab.key ? 'primary' : 'default'}
                size="small"
                onClick={() => handleStatusChange(tab.key)}
                style={{ 
                  flex: 1,
                  height: 32,
                  fontSize: 11,
                  fontWeight: tab.key === 'all' ? 600 : 400
                }}
              >
                {tab.label} {tab.count > 0 && `(${tab.count})`}
              </Button>
            ))}
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div style={{ padding: '16px 16px 0' }}>
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
            />
          </div>
        )}

        {/* Task List */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: 200 
            }}>
              <Spin tip="Loading tasks..." />
            </div>
          ) : (
            <TaskList 
              tasks={tasks}
              selectedStatus={selectedStatus}
              onRefresh={handleRefresh}
            />
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default TaskDrawer;
